import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { spacing, smallShadow } from 'ui/theme';

import QueryBuilder from 'modules/queryBuilder/legacyQueryBuilder';
import {
  fetchQueryResponse,
  updateFieldsForPropertyResponse,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { selectPresentState } from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import {
  FIELDS_MATCH_EMPTY_CRITERIA_PROPERTY,
  FIELDS_MATCH_EMPTY_SEARCH_GROUP_WITH_ALL,
  FIELDS_MATCH_EMPTY_SEARCH_QUERY,
} from 'modules/workflowNodeEditors/components/fieldsMatchEditor/constants';
import { formatFields } from 'modules/workflowNodeEditors/components/fieldsMatchEditor/FieldsMatchQueryBuilder.utils';
import { SearchQuery } from 'modules/workflows';
import { fetchEventFieldMappingsRequest, getEventFieldMappings } from 'modules/workflows/ducks';

import {
  ALLOWED_PROPERTIES,
  criteriaDropdownStaticComparators,
} from './CriteriaQueryBuilder.constants';
import { filterMappings, getIncludedMappings } from './CriteriaQueryBuilder.utils';

const QueryBuilderWrapper = styled.div`
  .cssQueryBuilderContainer {
    padding: ${spacing.plusHalf};
    border-radius: ${spacing.half};
    ${smallShadow};
  }
`;
interface CriteriaQueryBuilderProps {
  /** is the query builder read only? */
  isReadOnly?: boolean;
  /** callback that fires when the search query changes */
  onChange?: (value: string) => void;
  /**
   * initial search query string
   */
  searchQuery?: SearchQuery;
}

const CriteriaQueryBuilder = ({
  isReadOnly,
  onChange = noop,
  searchQuery: originalSearchQuery,
}: CriteriaQueryBuilderProps) => {
  const dispatch = useDispatch();
  const mappings = useSelector(getEventFieldMappings);

  // selectPresentState gets the up-to-date redux state for the QueryBuilder component
  // which is the exact data structure we need to update `searchQuery` in the
  // criteria form
  const searchQueryState = useSelector(selectPresentState);

  useEffect(() => {
    dispatch(fetchEventFieldMappingsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (mappings) {
      // only include the mappings that are needed for the CriteriaQueryBuilder
      // to reduce the number of iterations needed for the parser
      const includedMappings = getIncludedMappings(mappings);
      const formattedFieldMappings = formatFields(includedMappings, {
        filterMappings,
      });
      dispatch(updateFieldsForPropertyResponse(formattedFieldMappings));
    }
  }, [mappings, dispatch]);

  useEffect(() => {
    /**
     * WHAT A HACKY THING TO DO, but...
     * The <QueryBuilder /> component initializes itself with an empty block.
     * Without this timeout, the dispatch fires before the that init happens and
     * we end up with the imported info AND an additional empty block when all
     * we want is the imported info.
     */
    const loadingTimeout = setTimeout(() => {
      if (!isEmpty(originalSearchQuery)) {
        // @ts-ignore
        dispatch(fetchQueryResponse(originalSearchQuery));
      }
    }, 0);
    // cleanup
    return () => clearTimeout(loadingTimeout);
  }, [originalSearchQuery, dispatch]);

  useEffect(() => {
    onChange(searchQueryState);
  }, [searchQueryState, onChange]);

  return (
    <QueryBuilderWrapper>
      <QueryBuilder
        comparatorDropdownProps={criteriaDropdownStaticComparators}
        canSetMaxPropertyCount={false}
        emptyCriteriaProperty={FIELDS_MATCH_EMPTY_CRITERIA_PROPERTY}
        emptySearchGroup={FIELDS_MATCH_EMPTY_SEARCH_GROUP_WITH_ALL}
        emptySearchQuery={FIELDS_MATCH_EMPTY_SEARCH_QUERY}
        isReadOnly={isReadOnly}
        restrictToProperties={ALLOWED_PROPERTIES}
        showStateButtons={false}
      />
    </QueryBuilderWrapper>
  );
};

export default CriteriaQueryBuilder;
